/**
 * Sheet editor for condition_group nodes in the DAG flow builder.
 * Allows managing branches, conditions (AND-joined), and fallback steps.
 */
import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2, Plus } from 'lucide-react';
import {
  getConditionFields,
  getOperatorsForType,
  type ConditionField,
} from '@/lib/dag-condition-fields';
import type {
  ConditionGroupConfig,
  ConditionBranch,
  ConditionClause,
  ConditionOperator,
} from '@/lib/dag-types';

interface ConditionEditorSheetProps {
  open: boolean;
  onClose: () => void;
  config: ConditionGroupConfig;
  triggerType: string;
  onSave: (config: ConditionGroupConfig) => void;
  onDelete?: () => void;
}

function ClauseEditor({
  clause,
  fields,
  onChange,
  onRemove,
}: {
  clause: ConditionClause;
  fields: ConditionField[];
  onChange: (clause: ConditionClause) => void;
  onRemove: () => void;
}) {
  const field = fields.find((f) => f.value === clause.field);
  const fieldType = field?.type ?? 'string';
  const operators = getOperatorsForType(fieldType);

  return (
    <div className="flex items-start gap-1.5">
      {/* Field */}
      <Select
        value={clause.field}
        onValueChange={(v) => {
          const newField = fields.find((f) => f.value === v);
          onChange({ ...clause, field: v, value: newField?.type === 'number' ? 0 : '', operator: 'eq' });
        }}
      >
        <SelectTrigger className="h-7 text-xs flex-1 min-w-0">
          <SelectValue placeholder="Field" />
        </SelectTrigger>
        <SelectContent>
          {fields.map((f) => (
            <SelectItem key={f.value} value={f.value}>
              {f.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Operator */}
      <Select
        value={clause.operator}
        onValueChange={(v) => onChange({ ...clause, operator: v as ConditionOperator })}
      >
        <SelectTrigger className="h-7 text-xs w-16 shrink-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op.value} value={op.value}>
              {op.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value */}
      {fieldType === 'enum' && field?.options ? (
        <Select
          value={String(clause.value ?? '')}
          onValueChange={(v) => onChange({ ...clause, value: v })}
        >
          <SelectTrigger className="h-7 text-xs flex-1 min-w-0">
            <SelectValue placeholder="Value" />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : clause.operator === 'between' ? (
        <div className="flex gap-1 flex-1 min-w-0">
          <Input
            className="h-7 text-xs"
            type="number"
            value={String(clause.value ?? '')}
            onChange={(e) => onChange({ ...clause, value: Number(e.target.value) })}
            placeholder="Min"
          />
          <Input
            className="h-7 text-xs"
            type="number"
            value={String(clause.value_end ?? '')}
            onChange={(e) => onChange({ ...clause, value_end: Number(e.target.value) })}
            placeholder="Max"
          />
        </div>
      ) : (
        <Input
          className="h-7 text-xs flex-1 min-w-0"
          type={fieldType === 'number' ? 'number' : 'text'}
          value={String(clause.value ?? '')}
          onChange={(e) =>
            onChange({
              ...clause,
              value: fieldType === 'number' ? Number(e.target.value) : e.target.value,
            })
          }
          placeholder="Value"
        />
      )}

      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
        onClick={onRemove}
      >
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  );
}

function BranchEditor({
  branch,
  index,
  fields,
  onChange,
  onRemove,
  canRemove,
}: {
  branch: ConditionBranch;
  index: number;
  fields: ConditionField[];
  onChange: (branch: ConditionBranch) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const updateClause = (ci: number, clause: ConditionClause) => {
    const conditions = branch.conditions.map((c, i) => (i === ci ? clause : c));
    onChange({ ...branch, conditions });
  };

  const removeClause = (ci: number) => {
    onChange({ ...branch, conditions: branch.conditions.filter((_, i) => i !== ci) });
  };

  const addClause = () => {
    const defaultField = fields[0];
    onChange({
      ...branch,
      conditions: [
        ...branch.conditions,
        {
          field: defaultField?.value ?? '',
          operator: 'gt',
          value: defaultField?.type === 'number' ? 0 : '',
        },
      ],
    });
  };

  return (
    <div className="rounded-lg border border-border bg-wt-bg-tertiary/30 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <Input
          className="h-7 text-xs font-semibold w-40"
          value={branch.label}
          onChange={(e) => onChange({ ...branch, label: e.target.value })}
        />
        {canRemove && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive"
            onClick={onRemove}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>

      <div className="space-y-1.5">
        {branch.conditions.map((clause, ci) => (
          <div key={ci}>
            {ci > 0 && (
              <span className="text-[10px] font-semibold text-muted-foreground uppercase ml-1">
                AND
              </span>
            )}
            <ClauseEditor
              clause={clause}
              fields={fields}
              onChange={(c) => updateClause(ci, c)}
              onRemove={() => removeClause(ci)}
            />
          </div>
        ))}
      </div>

      <Button variant="ghost" size="sm" className="text-xs h-6" onClick={addClause}>
        <Plus className="mr-1 h-3 w-3" /> Add condition
      </Button>

      {branch.then_steps.length > 0 && (
        <div className="text-xs text-muted-foreground">
          Then: {branch.then_steps.map((s) => s.title).join(', ')}
        </div>
      )}
    </div>
  );
}

export function ConditionEditorSheet({
  open,
  onClose,
  config,
  triggerType,
  onSave,
  onDelete,
}: ConditionEditorSheetProps) {
  const [branches, setBranches] = useState<ConditionBranch[]>(config.branches);
  const [title, setTitle] = useState(config.title ?? 'Condition');
  const fields = getConditionFields(triggerType);

  useEffect(() => {
    if (open) {
      setBranches(config.branches);
      setTitle(config.title ?? 'Condition');
    }
  }, [open, config]);

  const updateBranch = (index: number, branch: ConditionBranch) => {
    setBranches((prev) => prev.map((b, i) => (i === index ? branch : b)));
  };

  const removeBranch = (index: number) => {
    if (branches.length <= 1) return;
    setBranches((prev) => prev.filter((_, i) => i !== index));
  };

  const addBranch = () => {
    setBranches((prev) => [
      ...prev,
      {
        label: `Branch ${String.fromCharCode(65 + prev.length)}`,
        conditions: [],
        then_steps: [],
      },
    ]);
  };

  const handleSave = () => {
    onSave({ ...config, title, branches });
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[460px] sm:max-w-[460px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Condition</SheetTitle>
          <SheetDescription>
            Define branches with conditions. First matching branch executes.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div className="space-y-1">
            <Label className="text-xs">Title</Label>
            <Input
              className="h-8 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {branches.map((branch, i) => (
            <BranchEditor
              key={i}
              branch={branch}
              index={i}
              fields={fields}
              onChange={(b) => updateBranch(i, b)}
              onRemove={() => removeBranch(i)}
              canRemove={branches.length > 1}
            />
          ))}

          <Button variant="outline" size="sm" className="w-full" onClick={addBranch}>
            <Plus className="mr-1 h-3 w-3" /> Add Branch
          </Button>
        </div>

        <SheetFooter className="mt-6 flex justify-between">
          <div>
            {onDelete && (
              <Button variant="destructive" size="sm" onClick={onDelete}>
                <Trash2 className="mr-1 h-3 w-3" /> Delete Condition
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save Condition</Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
